import { KeyboardEvent, useRef } from 'react';
import { MOOD_SCALE } from '../lib/constants';
import { useI18n } from '../i18n';

interface Props {
  value: number | null;
  onChange: (value: number) => void;
  labelledBy: string;
  describedBy?: string;
}

// The design system's score faces (MOOD_SCALE's emoji), reused for any 1–5
// rating — not mood-specific. Same ARIA radio-group pattern as MoodPicker
// (one Tab stop, arrow keys move and select), but with no per-value word:
// callers with specific scale meanings (workload, energy, …) supply their
// own "1 = X, 5 = Y" hint via describedBy instead.
export function ScalePicker({ value, onChange, labelledBy, describedBy }: Props) {
  const { t } = useI18n();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const focusIndex = value ? value - 1 : 0;

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, i: number) {
    const last = MOOD_SCALE.length - 1;
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next < 0) return;
    e.preventDefault();
    onChange(MOOD_SCALE[next].value);
    refs.current[next]?.focus();
  }

  return (
    <div className="sx-scale" role="radiogroup" aria-labelledby={labelledBy} aria-describedby={describedBy}>
      {MOOD_SCALE.map((m, i) => {
        const checked = value === m.value;
        return (
          <button
            key={m.value}
            ref={(el) => (refs.current[i] = el)}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={t['r3.me.pickedOf'](m.value)}
            tabIndex={i === focusIndex ? 0 : -1}
            className={`sx-tile${checked ? ' active' : ''}`}
            onClick={() => onChange(m.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {checked && <span className="tick" aria-hidden="true">✓</span>}
            <span className="emoji" aria-hidden="true">{m.emoji}</span>
            <span className="lbl" aria-hidden="true">{m.value}</span>
          </button>
        );
      })}
    </div>
  );
}
